/**
 * FilterBar quick + more filter definitions for the operations dashboard's
 * active tab (maintenance status / alarm status / structure type / alarm
 * tree), plus the matching "reset all" handler. `alarmStatus` and
 * `maintenanceStatus` use an internal 'all' sentinel — this hook adapts
 * that to/from the single-select chip's null-means-unset convention.
 *
 * Contains:
 * - useOperationsQuickFilters — default export
 */

import NotificationsIcon from '@mui/icons-material/Notifications';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import React, { useCallback, useMemo } from 'react';

const MAINTENANCE_STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'done', label: 'Closed' },
];

/**
 * @param {object} opts
 * @param {string} opts.activeTab - 'alarms' | 'maintenance'.
 * @param {boolean} opts.isBillboard - gates the Digital/Static structure-type filter.
 * @param {string} opts.alarmStatus - 'all' | 'active' | 'resolved'.
 * @param {Function} opts.setAlarmStatus
 * @param {string} opts.maintenanceStatus - 'all' | 'open' | 'done'.
 * @param {Function} opts.setMaintenanceStatus
 * @param {string|null} opts.structureFilter - 'digital' | 'static' | null.
 * @param {Function} opts.setStructureFilter
 * @param {string[]} opts.selectedAlarms - selected Alarm.key values (the Alarms tree filter).
 * @param {Function} opts.setSelectedAlarms
 * @param {{value: string, label: string, children: {value: string, label: string}[]}[]} opts.alarmTreeOptions -
 *   category>alarm tree nodes (see alarmTreeOptions.js buildAlarmTreeOptions).
 * @param {Function} opts.t - i18next translator, for the Alarms filter's label/count text.
 * @returns {{quickFilters: object[], moreFilters: object[], handleResetGridFilters: Function}}
 */
export default function useOperationsQuickFilters({
  activeTab,
  isBillboard,
  alarmStatus,
  setAlarmStatus,
  maintenanceStatus,
  setMaintenanceStatus,
  structureFilter,
  setStructureFilter,
  selectedAlarms,
  setSelectedAlarms,
  alarmTreeOptions,
  t,
}) {
  const quickFilters = useMemo(() => {
    if (activeTab === 'maintenance') {
      return [
        {
          key: 'maintenanceStatus',
          label: 'Status',
          type: 'single',
          value: maintenanceStatus === 'all' ? null : maintenanceStatus,
          onChange: v => setMaintenanceStatus(v ?? 'all'),
          icon: <TaskAltIcon sx={{ fontSize: 16 }} />,
          options: MAINTENANCE_STATUS_OPTIONS.filter(o => o.key !== 'all').map(o => ({
            value: o.key,
            label: o.label,
          })),
        },
      ];
    }

    const filters = [];
    if (activeTab === 'alarms') {
      filters.push({
        key: 'alarmStatus',
        label: 'Status',
        type: 'single',
        value: alarmStatus === 'all' ? null : alarmStatus,
        onChange: v => setAlarmStatus(v ?? 'all'),
        icon: <TaskAltIcon sx={{ fontSize: 16 }} />,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'resolved', label: 'Resolved' },
        ],
      });
    }
    if (isBillboard) {
      filters.push({
        key: 'structureType',
        label: 'Type',
        type: 'single',
        value: structureFilter,
        onChange: setStructureFilter,
        icon: <SplitscreenIcon sx={{ fontSize: 16 }} />,
        options: [
          { value: 'digital', label: 'Digital' },
          { value: 'static', label: 'Static' },
        ],
      });
    }
    return filters;
  }, [activeTab, alarmStatus, maintenanceStatus, structureFilter, isBillboard]);

  // The alarm tree lives behind More filters — a category>alarm picker reads
  // as a deeper interaction than the inline chips, matching how the site-group
  // grid's own alarm-category filter is placed.
  const moreFilters = useMemo(() => {
    if (activeTab !== 'alarms') return [];
    return [
      {
        key: 'alarms',
        label: t('operationsDashboard.filterAlarms', { defaultValue: 'Alarms' }),
        type: 'tree',
        value: selectedAlarms,
        onChange: setSelectedAlarms,
        icon: <NotificationsIcon sx={{ fontSize: 16 }} />,
        options: alarmTreeOptions,
        countLabel: count =>
          t('operationsDashboard.alarmsSelectedCount', {
            count,
            defaultValue: count === 1 ? '1 alarm' : `${count} alarms`,
          }),
      },
    ];
  }, [activeTab, t, selectedAlarms, setSelectedAlarms, alarmTreeOptions]);

  const handleResetGridFilters = useCallback(() => {
    if (activeTab === 'maintenance') {
      setMaintenanceStatus('all');
      return;
    }
    setAlarmStatus('all');
    setStructureFilter(null);
    setSelectedAlarms([]);
  }, [activeTab, setAlarmStatus, setStructureFilter, setSelectedAlarms, setMaintenanceStatus]);

  return { quickFilters, moreFilters, handleResetGridFilters };
}
